import { Component, OnInit, AfterViewInit, ViewChild, EventEmitter, OnDestroy, ChangeDetectorRef, HostBinding, Host, ViewChildren, QueryList } from '@angular/core';
import { Subscription } from 'rxjs';

// ---------Shared--------------------------------------------
import { StudentService } from '../../shared-components/student/student.service';
import { Student } from '../../shared-components/student/student.model';
import { AcademicHistory } from '../../shared-components/student/academic-history.model';
import { YearService } from '../../shared-components/year/year.service';
import { Year } from '../../shared-components/year/year.model';
import { UserService } from '../../shared-components/user/user.service';
import { FilterComponent } from '../../shared-components/controls/filter/filter.component';
import { TylerMatTableDataSource } from '../../shared-components/angular-material/extended.class';
import { AriaDirective } from '../../shared-components/directives/aria.directive';
import { detailExpand } from '../../shared-components/angular-material/mat-animations';

// -------Angular Material------------------------------------
import { MatSort } from '@angular/material/sort';
import { AcademicHistorySummary } from '../../shared-components/student/academic-history-summary.model';
import { formatDate } from '@angular/common';


@Component({
    selector: 'academic-history',
    templateUrl: './academic-history.component.html',
    styleUrls: ['./academic-history.component.css'],
    // Animation for when the detail row is expanded or collapsed
    animations: [detailExpand],
})
export class AcademicHistoryComponent implements OnInit, AfterViewInit, OnDestroy {

    @HostBinding('class.sis-app-root') sisAppRoot: Host = true;
    // ------------Academic History Data---------------------------
    student360YearSubscription: Subscription;
    s360Year: Year;
    selectedStudentSubscription: Subscription;
    selectedStudent: Student;
    academicHistory: AcademicHistory[] = [];
    academicHistorySummary: AcademicHistorySummary = new AcademicHistorySummary();
    semesters: string[] = [];
    // ------------Selection/Flags-----------------------------------
    V10: boolean;
    isShowHSTranscript = false;
    isBusy = false;
    // ----------Angular Material------------------------------------
    angularMatData = new TylerMatTableDataSource();
    @ViewChild(MatSort) sort: MatSort;
    columnsToDisplay: string[] = ['More', 'AcademicYear', 'School', 'Gradelevel', 'Course', 'AttemptedCredit', 'EarnedCredit', 'Final'];
    columnsToDisplayDetailRow: string[] = ['CourseSection', 'IncludeInGPA', 'Teacher', 'TransferredFrom', 'CourseComplete'];
    toggleAllRows: EventEmitter<boolean> = new EventEmitter<boolean>();
    filterReset = '';
    @ViewChildren(FilterComponent) filterRef: QueryList<FilterComponent>;
    clearFilterEvent = new EventEmitter<any>();
    userType: number;
    // --------------LifeCycle---------------------------------------

    constructor(private studentService: StudentService,
        private yearService: YearService,
        private userService: UserService,
        public cdRef: ChangeDetectorRef) {
        // Subscribe to services so that the component will be notified any time the value is changed by another component
        // When the value has changed the inner code will be executed
        this.selectedStudentSubscription = studentService.selectedStudentStream$.subscribe(
            studentId => {
                // Reload student data when the selected student is changed (Student button in the header)
                this.loadStudentInfo(studentId);
            });
        this.student360YearSubscription = yearService.observableStudent360YearStream$.subscribe(
            s60Year => {
                // Reload AcademicHistory when the selected year is changed
                this.getAcademicHistory();
            }
        );

        // Load data into a DataSource object
        // MatTableDataSource extends DataSource to allow sorting, pagination and basic filtering
        // TylerMatTableDataSource extends MatTableDataSource and allows complex filtering and detail rows
        this.angularMatData.data = this.academicHistory;

        // Copy flag from service to local variable
        // This prevents having to directly call a service from the html, keeping the view loosely coupled
        this.V10 = userService.currentUser.V10;
        this.userType = this.userService.userType;
    }

    ngOnInit() {
        // Initial data load
        this.loadStudentInfo(this.studentService.selectedStudent.Id);
    }

    ngAfterViewInit() {
        // Call any methods that require data to already be available

    }

    ngOnDestroy() {
        // Remove all subscriptions when user navigates to a different page
        this.selectedStudentSubscription.unsubscribe();
        this.student360YearSubscription.unsubscribe();
    }

    // ------------Academic History Data---------------------------

    loadStudentInfo(studentId: number) {
        if (this.studentService.studentEnrollment) {
            this.clearStudentData();
            this.checkStudentGradeLevel(this.studentService.studentEnrollment.GradeLevel);
            this.selectedStudent = this.studentService.students.filter(function (student) {
                return student.Id == studentId;
            })[0];
            this.getAcademicHistory();
            this.getAcademicHistorySummary();
        }
    }

    getAcademicHistory() {
        this.isBusy = true;
        this.angularMatData.data = [];
        setTimeout(() => {
            this.studentService.getAcademicHistory(this.selectedStudent.Id, this.yearService.student360Year.Id, this.isShowHSTranscript).subscribe(data => {
                this.academicHistory = data;
                this.loadAcademicHistory();
                this.generateSemesterColumns();
            });
        }, 0);
    }

    getAcademicHistorySummary() {
        this.isBusy = true;
        setTimeout(() => {
            this.studentService.getAcademicHistorySummary(this.selectedStudent.Id, this.yearService.student360Year.Id).subscribe(data => {
                this.academicHistorySummary = data;
            });
        }, 0);
    }

    generateSemesterColumns() {
        //  Dynamically generates the semester columns based on data returned from the server

        // Remove 'Final' column from array
        // Makes the array simpler to work with as the semester columns are now at the end of the array
        let index = this.columnsToDisplay.indexOf('Final');
        if (index != -1) {
            this.columnsToDisplay.splice(index, 1);
        }


        for (let i = 0; i < this.semesters.length; i++) {
            index = this.columnsToDisplay.indexOf(this.semesters[i]);
            if (index != -1) {
                this.columnsToDisplay.splice(index, 1);
            }
        }

        this.semesters = [];

        for (let academicHistory of this.academicHistory) {
            for (let semester of academicHistory.AHSemester.filter(s => s.Description != 'Final')) {
                if (this.semesters.indexOf(semester.Description) == -1) {
                    this.semesters.push(semester.Description);
                    this.columnsToDisplay.push(semester.Description);
                }
            }
        }

        if (this.V10) {
            this.columnsToDisplay.push('Final');
        }
    }

    private loadAcademicHistory(): void {
        // load data into Angular Material table
        this.angularMatData.data = this.academicHistory;
        this.angularMatData.useFilterDataAccessor = true;

        setTimeout(() => {
            //  Custom sorting for case here Academic Year starts with letters
            //  Extracts last two characters and returns the resulting substring if it is a number
            this.angularMatData.sortingDataAccessor = (item: any, property: string) => {
                if (property == 'AcademicYear') {
                    if (isNaN(parseInt(item.AcademicYear, 10))) {
                        const subStrAcademicYear: string = item.AcademicYear.substr(item.AcademicYear.length - 2);
                        if (!isNaN(parseInt(subStrAcademicYear, 10))) {
                            return subStrAcademicYear;
                        }
                    }
                    return item[property];
                }
                //  Custom sorting for dynamic columns S1, S2, etc
                else if (property.startsWith('Sem')) {
                    const index: number = parseInt(property.substr(property.length - 1), 10);
                    if (item.AHSemester[index]) {
                        const semValue = parseInt(item.AHSemester[index].Grade, 10);
                        if (!isNaN(semValue)) {
                            return semValue;
                        }
                        else {
                            return item.AHSemester[index].Grade;
                        }
                    }
                }
                else if (property == 'Final') {
                    const finalValue = parseInt(item.Final, 10);
                    if (!isNaN(finalValue)) {
                        return finalValue;
                    }
                    else {
                        return item.Final;
                    }
                }
                //  Default sorting
                else {
                    return this.angularMatData.defaultSortingAccessor(item, property);
                }
            };

            this.angularMatData.sort = this.sort;
        }, 0);

        this.isBusy = false;
    }

    checkStudentGradeLevel(gradeLevel: string) {
        if (gradeLevel == '09' || gradeLevel == '10' || gradeLevel == '11' || gradeLevel == '12') {
            this.isShowHSTranscript = true;
        } else {
            this.isShowHSTranscript = false;
        }
    }

    clearStudentData() {

    }

    // ------------Aria/Accessibility---------------------------

    getAriaRow(row: AcademicHistory): string {
        // Returns string for a given row's data
        // reads each column header name and then the cell data for that column
        const colList: string[] = ['Academic Year',
            'School',
            'Grade Level',
            'Course Name',
            'Attempted Credits',
            'Earned Credits'];

        this.semesters.forEach(element => {
            colList.push(element);
        });

        colList.push('Final');

        const dataList: string[] = [row.AcademicYear,
        row.School,
        row.Gradelevel,
        row.Course,
        row.AttemptedCredit.toString(),
        row.EarnedCredit.toString()];
        row.AHSemester.forEach(element => {
            dataList.push(element.Grade);
        });

        dataList.push(row.Final);

        return AriaDirective.concatAriaStrings(colList, dataList) + ' Expand for more details';
    }

    // ----------Angular Material------------------------------------
    // These methods are used to allow calls to the TylerMatTableDataSource methods from the html
    toggleExpanded(row: any) {
        // Toggles row between expanded and collapsed
        this.angularMatData.toggleExpanded(row);
    }

    checkExpanded(row: any): boolean {
        // Return true if expanded and false if collapsed
        return this.angularMatData.checkExpanded(row);
    }

    expandAll(expand: boolean) {
        //  Expand or collapse all rows
        //  true => expand all    false => collapse all
        this.angularMatData.expandAll(expand);
    }

    rowKeyPress(event: KeyboardEvent, row: any) {
        this.angularMatData.rowKeyPress(event, row);
    }

    getFormattedDate(date) {
        return formatDate(date, 'MM/dd/yy', 'en-US');
    }

}